export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  // Client-computed elapsed (started_at vs local now) can go negative under
  // clock skew; clamp so a live badge never renders e.g. "-1.2s".
  if (ms < 0) ms = 0;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 60 * 60 * 24 * 365],
  ["month", 60 * 60 * 24 * 30],
  ["day", 60 * 60 * 24],
  ["hour", 60 * 60],
  ["minute", 60],
  ["second", 1],
];

const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  // An empty/invalid ISO (e.g. a repo with last_run_status set but last_run_at
  // still null, passed as `?? ""`) makes `then` NaN, and
  // Intl.RelativeTimeFormat.format(NaN) throws a RangeError that crashes the
  // whole render subtree. Guard like formatAbsolute does.
  if (Number.isNaN(then)) return "—";
  const diffSec = Math.round((then - now.getTime()) / 1000);
  const absSec = Math.abs(diffSec);
  for (const [unit, secondsInUnit] of UNITS) {
    if (absSec >= secondsInUnit || unit === "second") {
      return rtf.format(Math.round(diffSec / secondsInUnit), unit);
    }
  }
  return rtf.format(diffSec, "second");
}

export function elapsedMs(startedAtIso: string, now: Date = new Date()): number {
  return now.getTime() - new Date(startedAtIso).getTime();
}

/**
 * Full local wall-clock timestamp for tooltips — pairs with `formatRelative`
 * so operators can read the exact time behind "5 minutes ago" when correlating
 * a run with external logs. Returns the raw input if it isn't a valid date.
 */
export function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/**
 * Local wall-clock time of day to the millisecond ("14:02:05.123") for the
 * run-detail event log. Local, not UTC, so a line reads against the run's
 * "started" time and every other timestamp on the page; the date is left to
 * `formatAbsolute` on hover. "—" for an invalid date, like `formatRelative`.
 */
export function formatClockMs(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

/**
 * Forward-looking countdown ("in 5h 23m", "in 12m", "in <1m", "due now").
 * Granularity tightens as the target gets closer so the < 1 h case can tick
 * each minute without flicker further out.
 */
export function formatTimeUntil(target: Date, now: Date = new Date()): string {
  const diffMs = target.getTime() - now.getTime();
  if (diffMs <= 0) return "due now";
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "in <1m";
  const min = Math.floor(sec / 60);
  if (min < 60) return `in ${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  if (hr < 24) return remMin ? `in ${hr}h ${remMin}m` : `in ${hr}h`;
  const day = Math.floor(hr / 24);
  const remHr = hr % 24;
  return remHr ? `in ${day}d ${remHr}h` : `in ${day}d`;
}
